import { useEffect, useState, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutGrid, Map, Hand, Car, Users, Globe, UsersRound, Archive,
  Wallet, Megaphone, Presentation, ShieldCheck, AlertTriangle, MessageSquare,
  Bell, FileText, Settings, LogOut, ChevronLeft, ShieldQuestion, Loader2, Gauge, Mail,
} from "lucide-react";
import { useAdminAuth } from "./AdminAuth";
import { adminApi, formatApiError } from "./api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { newCount, SEEN_EVENT } from "./seen";
import { Toaster } from "../components/ui/sonner";
import { toast } from "sonner";

const NAV = [
  { section: null, items: [] }, // home group computed per role below
  {
    section: "Operations",
    items: [
      { to: "/admin/rides", label: "Ride Management", icon: Map, perm: "Manage schedules" },
      { to: "/admin/users", label: "User Management", icon: Users, badge: "verifications", dot: "verifications", perm: "Manage access" },
    ],
  },
  {
    section: "Community",
    items: [
      { to: "/admin/community", label: "Community Management", icon: UsersRound, perm: "Full system access" },
    ],
  },
  {
    section: "Finance",
    items: [
      { to: "/admin/payments", label: "Payments & Finance", icon: Wallet, badge: "finance_new", perm: "Manage payroll" },
      { to: "/admin/marketing", label: "Promotions & Marketing", icon: Megaphone, perm: "Manage promotions" },
    ],
  },
  {
    section: "Safety & Trust",
    items: [
      { to: "/admin/m/safety-center", label: "Safety Center", icon: ShieldCheck, badge: "sos", perm: "Manage support" },
      { to: "/admin/fraud-alerts", label: "Fraud Alerts", icon: AlertTriangle, badge: "fraud", perm: "Full system access" },
    ],
  },
  {
    section: "Support",
    items: [
      { to: "/admin/support", label: "Customer Support", icon: MessageSquare, badge: "tickets", dot: "support", perm: "Manage support" },
    ],
  },
  {
    section: "System",
    items: [
      { to: "/admin/employees", label: "Employees", icon: Users, perm: "Full system access" },
      { to: "/admin/settings", label: "Settings", icon: Settings, perm: "Full system access" },
    ],
  },
];

// Topbar notification bell — role-respective feed for every staff member and admin.
const notifTimeAgo = (iso) => {
  if (!iso) return "";
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)} min ago`;
  if (s < 86400) return `${Math.floor(s / 3600)} hr ago`;
  return new Date(iso).toLocaleDateString("en-CA", { month: "short", day: "numeric" });
};

const NOTIF_ICONS = {
  authorization: ShieldQuestion, note: Mail, board: Megaphone,
  finance: Wallet, support: MessageSquare, users: Users,
};

function NotificationBell() {
  const [data, setData] = useState({ items: [], unread: 0 });
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, right: 8 });
  const btnRef = useRef(null);
  const panelRef = useRef(null);
  const navigate = useNavigate();

  const load = useCallback(() => {
    adminApi.get("/notifications").then((r) => setData(r.data)).catch(() => {});
  }, []);
  useEffect(() => {
    load();
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, [load]);
  useEffect(() => {
    const close = (e) => {
      if (btnRef.current?.contains(e.target) || panelRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  // Rendered in a portal with position:fixed so the sticky topbar's stacking
  // context (backdrop-blur) can never trap the panel behind page content.
  const toggle = () => {
    if (!open && btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      setPos({ top: r.bottom + 8, right: Math.max(8, window.innerWidth - r.right) });
    }
    setOpen((v) => !v);
  };

  const markAll = async () => {
    try { await adminApi.post("/notifications/read", { all: true }); load(); } catch { /* ignore */ }
  };
  const openItem = (n) => {
    if (!n.read) adminApi.post("/notifications/read", { ids: [n.id] }).then(load).catch(() => {});
    setOpen(false);
    if (n.link) navigate(n.link);
  };

  return (
    <>
      <button ref={btnRef} data-testid="notif-bell" onClick={toggle} title="Notifications"
        className="relative grid h-10 w-10 place-items-center rounded-full border border-[#12142b]/10 bg-white text-[#3a3f57] transition-colors hover:bg-[#f4f6fb]">
        <Bell className="h-5 w-5" strokeWidth={1.75} />
        {data.unread > 0 && (
          <span data-testid="notif-badge" className="absolute -right-1 -top-1 grid h-5 min-w-[20px] place-items-center rounded-full bg-[#e4002b] px-1 text-[10px] font-bold text-white">
            {data.unread}
          </span>
        )}
      </button>
      {open && createPortal(
        <div ref={panelRef} data-testid="notif-panel" style={{ top: pos.top, right: pos.right, zIndex: 90 }}
          className="fixed w-[380px] max-w-[calc(100vw-16px)] overflow-hidden rounded-2xl border border-[#12142b]/10 bg-white shadow-xl">
          <div className="flex items-center justify-between border-b border-[#12142b]/8 px-4 py-3">
            <p className="font-display text-sm font-bold text-[#12142b]">Notifications</p>
            {data.unread > 0 && (
              <button data-testid="notif-mark-all" onClick={markAll} className="text-xs font-semibold text-[#2340b5] hover:underline">Mark all read</button>
            )}
          </div>
          <div className="max-h-[420px] overflow-y-auto">
            {data.items.length === 0 ? (
              <p className="py-10 text-center text-sm text-[#565b73]" data-testid="notif-empty">No notifications yet.</p>
            ) : data.items.map((n) => {
              const Icon = NOTIF_ICONS[n.kind] || Bell;
              return (
                <button key={n.id} data-testid={`notif-item-${n.id}`} onClick={() => openItem(n)}
                  className={`flex w-full items-start gap-3 border-b border-[#12142b]/5 px-4 py-3 text-left transition-colors last:border-0 hover:bg-[#f4f6fb] ${n.read ? "opacity-60" : ""}`}>
                  <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-[#2340b5]/8"><Icon className="h-4 w-4 text-[#2340b5]" /></span>
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center gap-2">
                      <span className="truncate text-sm font-semibold text-[#12142b]">{n.title}</span>
                      {!n.read && <span className="h-2 w-2 shrink-0 rounded-full bg-[#2340b5]" />}
                    </span>
                    <span className="block truncate text-xs text-[#565b73]">{n.body}</span>
                    <span className="mt-0.5 block text-[11px] text-[#565b73]/80">{notifTimeAgo(n.created_at)}</span>
                  </span>
                </button>
              );
            })}
          </div>
        </div>,
        document.body)}
    </>
  );
}

// Global "authorization required" prompt — opens whenever the backend blocks a
// staff action that a super admin has gated in Settings → General → Authorization Access.
function AuthorizationDialog() {
  const [req, setReq] = useState(null); // { action_key, action_label, message }
  const [note, setNote] = useState("");
  const [sending, setSending] = useState(false);
  const lastShown = useRef({});

  useEffect(() => {
    const h = (e) => {
      const d = e.detail || {};
      const now = Date.now();
      // Debounce: background polling can re-hit the same gated endpoint.
      if (lastShown.current[d.action_key] && now - lastShown.current[d.action_key] < 10 * 60 * 1000) return;
      lastShown.current[d.action_key] = now;
      setNote("");
      setReq(d);
    };
    window.addEventListener("sr-authorization-required", h);
    return () => window.removeEventListener("sr-authorization-required", h);
  }, []);

  const submit = async () => {
    setSending(true);
    try {
      const { data } = await adminApi.post("/authorization/requests", { action_key: req.action_key, note });
      if (data.already_pending) {
        toast.info("You already have a pending request for this action — the admin has been notified.");
      } else {
        toast.success("Authorization request sent to the admin");
      }
      setReq(null);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not send the request");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={!!req} onOpenChange={(o) => !o && setReq(null)}>
      <DialogContent className="max-w-md" data-testid="auth-required-dialog">
        <DialogHeader><DialogTitle>Authorization required</DialogTitle></DialogHeader>
        <div className="space-y-3 text-sm text-[#565b73]">
          <div className="flex items-start gap-3 rounded-xl bg-[#f59e0b]/8 p-3">
            <ShieldQuestion className="mt-0.5 h-5 w-5 shrink-0 text-[#b45309]" />
            <p>
              <span className="font-semibold text-[#12142b]">{req?.action_label}</span> requires admin authorization.
              Send a request to a super admin — approvals are one-time and valid for 15 minutes.
            </p>
          </div>
          <div>
            <label className="mb-1 block text-sm font-semibold text-[#12142b]">Note for the admin (optional)</label>
            <textarea
              data-testid="auth-request-note" rows={3} value={note} onChange={(e) => setNote(e.target.value)}
              placeholder="Why do you need this access?"
              className="w-full rounded-xl border border-[#12142b]/12 bg-[#f4f6fb] px-3 py-2.5 text-sm outline-none focus:border-[#2340b5]"
            />
          </div>
        </div>
        <DialogFooter className="mt-2 gap-2 sm:justify-between">
          <button data-testid="auth-request-cancel" onClick={() => setReq(null)} disabled={sending}
            className="rounded-xl border border-[#12142b]/12 px-4 py-2.5 text-sm font-semibold text-[#12142b] hover:bg-[#f4f6fb]">Not now</button>
          <button data-testid="auth-request-submit" onClick={submit} disabled={sending}
            className="inline-flex items-center gap-2 rounded-xl bg-[#2340b5] px-4 py-2.5 text-sm font-bold text-white hover:bg-[#1b3390] disabled:opacity-60">
            {sending && <Loader2 className="h-4 w-4 animate-spin" />} Request authorization
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminLayout() {
  const { admin, logout, hasPerm } = useAdminAuth();
  const location = useLocation();
  const [badges, setBadges] = useState({});
  const [financeNew, setFinanceNew] = useState(0);
  const [pendingVerif, setPendingVerif] = useState(0);
  const [sosActive, setSosActive] = useState(0);
  const [dots, setDots] = useState({ verifications: false, support: false });
  const prevVerif = useRef(null);
  const pathRef = useRef(location.pathname);
  const inbox = useRef({ verifUsers: [], tickets: [], chats: [] });

  // Recompute which nav items have unseen "new" inquiries (for the sidebar dot).
  const computeDots = () => {
    const v = newCount("verifications", inbox.current.verifUsers, (u) => u.id, (u) => u.pending_verification);
    const t = newCount("tickets", inbox.current.tickets);
    const c = newCount("chats", inbox.current.chats, (x) => x.id, (x) => x.unread_from_user);
    setDots({ verifications: v > 0, support: t + c > 0 });
  };

  // Recompute dots immediately when the admin opens an item (marks it seen).
  useEffect(() => {
    const h = () => computeDots();
    window.addEventListener(SEEN_EVENT, h);
    return () => window.removeEventListener(SEEN_EVENT, h);
  }, []);

  useEffect(() => {
    adminApi.get("/stats").then((r) => {
      setBadges({
        fraud: r.data.alerts.fraud_alerts,
        tickets: r.data.support.open_tickets,
        requests: 12,
      });
      setPendingVerif(r.data.alerts.pending_verifications || 0);
      prevVerif.current = r.data.alerts.pending_verifications || 0;
    }).catch(() => {});
  }, []);

  // Live SOS badge: poll the active emergency-alert count so an ongoing
  // emergency is visible from anywhere in the dashboard.
  useEffect(() => {
    const fetchSos = () => adminApi.get("/safety/active-count")
      .then((r) => setSosActive(r.data.active || 0)).catch(() => {});
    fetchSos();
    const id = setInterval(fetchSos, 30000);
    return () => clearInterval(id);
  }, []);

  // Clear the Payments badge when the user is on that page.
  useEffect(() => {
    pathRef.current = location.pathname;
  }, [location.pathname]);

  // Idle auto-logout based on the admin's Session Security setting (15/30/60 min).
  useEffect(() => {
    let timer;
    let timeoutMin = 30;
    const doLogout = () => { toast.error("Signed out due to inactivity"); logout(); };
    const reset = () => { clearTimeout(timer); timer = setTimeout(doLogout, timeoutMin * 60 * 1000); };
    const events = ["mousemove", "mousedown", "keydown", "scroll", "touchstart"];
    const loadSetting = () => {
      adminApi.get("/security/settings").then((r) => {
        timeoutMin = r.data.session_timeout_min || 30;
        reset();
      }).catch(() => reset());
    };
    events.forEach((e) => window.addEventListener(e, reset, { passive: true }));
    window.addEventListener("sr-security-settings-changed", loadSetting);
    loadSetting();
    return () => {
      clearTimeout(timer);
      events.forEach((e) => window.removeEventListener(e, reset));
      window.removeEventListener("sr-security-settings-changed", loadSetting);
    };
  }, [logout]);

  // Poll finance queues + pending verifications so new work surfaces from any screen.
  useEffect(() => {
    let stopped = false;
    const poll = async () => {
      try {
        const [ov, rf, stats] = await Promise.all([
          adminApi.get("/finance/overview", { params: { period: "7d" } }),
          adminApi.get("/finance/refunds", { params: { page: 1, limit: 500 } }),
          adminApi.get("/stats"),
        ]);
        // Persistent work-queue count on Payments & Finance:
        // pending e-transfer requests + completed trips ready for payout
        // + cancelled-booking refunds awaiting review (below-threshold refunds
        // are auto-approved, so anything left in processing needs attention).
        const attention =
          (ov.data.cards.etransfer_queue || 0) +
          (ov.data.cards.ready_for_payout?.count || 0) +
          (rf.data.items || []).filter((x) => x.status === "processing").length;
        if (!stopped) setFinanceNew(attention);

        const pv = stats.data.alerts.pending_verifications || 0;
        if (prevVerif.current !== null && !stopped && pv > prevVerif.current) {
          const dv = pv - prevVerif.current;
          toast.info(`${dv} new document${dv > 1 ? "s" : ""} submitted for verification`, {
            description: "Review them in User Management",
          });
        }
        prevVerif.current = pv;
        if (!stopped) {
          setPendingVerif(pv);
          setBadges((b) => ({
            ...b,
            fraud: stats.data.alerts.fraud_alerts,
            tickets: stats.data.support.open_tickets,
          }));
        }

        // Fetch inbox lists to compute unseen "new" dots on the sidebar.
        const [cases, chats, verifUsers] = await Promise.all([
          adminApi.get("/support/cases", { params: { page: 1 } }).catch(() => null),
          adminApi.get("/support/chats").catch(() => null),
          adminApi.get("/users", { params: { verification: "pending", status: "active", limit: 100 } }).catch(() => null),
        ]);
        if (!stopped) {
          inbox.current = {
            tickets: cases?.data?.items || [],
            chats: chats?.data || [],
            verifUsers: verifUsers?.data?.items || [],
          };
          computeDots();
        }
      } catch { /* ignore */ }
    };
    poll();
    const id = setInterval(poll, 30000);
    return () => { stopped = true; clearInterval(id); };
  }, []);

  const allBadges = { ...badges, finance_new: financeNew, verifications: pendingVerif, sos: sosActive };

  // Home group: Admin Dash is the landing page for super admins, Dashboard for staff.
  const isSuper = admin?.role === "super_admin";
  const nav = NAV.map((group, gi) => gi === 0
    ? {
        ...group,
        items: isSuper
          ? [
              { to: "/admin", label: "Admin Dash", icon: Gauge, end: true },
              { to: "/admin/dashboard", label: "Dashboard", icon: LayoutGrid },
            ]
          : [{ to: "/admin", label: "Dashboard", icon: LayoutGrid, end: true }],
      }
    : group);

  return (
    <div className="flex min-h-screen bg-[#f4f6fb]">
      {/* Sidebar */}
      <aside className="fixed inset-y-0 left-0 z-40 flex w-64 flex-col border-r border-[#12142b]/8 bg-white">
        <div className="flex items-center gap-2 border-b border-[#12142b]/8 px-6 py-5">
          <img src="/saferide-mark.png" alt="SafeRide" className="h-7 w-auto" />
          <div>
            <p className="font-display text-sm font-extrabold leading-none text-[#12142b]">SafeRide</p>
            <p className="mt-0.5 text-[10px] font-semibold uppercase tracking-wider text-[#565b73]">Admin Panel</p>
          </div>
        </div>

        <nav className="flex-1 overflow-y-auto px-3 py-4">
          {nav.map((group, gi) => {
            const visible = group.items.filter((it) => !it.perm || hasPerm(it.perm));
            if (!visible.length) return null;
            return (
            <div key={gi} className="mb-4">
              {group.section && (
                <p className="px-3 pb-2 pt-2 text-[10px] font-bold uppercase tracking-[0.14em] text-[#565b73]/70">
                  {group.section}
                </p>
              )}
              {visible.map((it) => (
                <NavLink
                  key={it.to}
                  to={it.to}
                  end={it.end}
                  data-testid={`nav-${it.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className={({ isActive }) =>
                    `group mb-0.5 flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
                      isActive
                        ? "bg-[#2340b5] text-white"
                        : "text-[#3a3f57] hover:bg-[#f4f6fb]"
                    }`
                  }
                >
                  {({ isActive }) => (
                    <>
                      <span className="relative">
                        <it.icon className="h-[18px] w-[18px]" strokeWidth={1.75} />
                        {it.dot && dots[it.dot] && (
                          <span
                            data-testid={`nav-dot-${it.dot}`}
                            className={`absolute -right-1 -top-1 h-2.5 w-2.5 rounded-full ring-2 ${isActive ? "ring-[#2340b5]" : "ring-white"} bg-[#f59e0b]`}
                          />
                        )}
                      </span>
                      <span className="flex-1">{it.label}</span>
                      {it.badge && allBadges[it.badge] > 0 && (
                        <span
                          data-testid={it.badge === "sos" ? "nav-sos-badge" : undefined}
                          className={`grid h-5 min-w-[20px] place-items-center rounded-full px-1 text-[11px] font-bold ${
                            isActive && it.badge !== "sos" ? "bg-white text-[#2340b5]" : "bg-[#e4002b] text-white"
                          } ${it.badge === "sos" ? "animate-pulse" : ""}`}
                        >
                          {allBadges[it.badge]}
                        </span>
                      )}
                    </>
                  )}
                </NavLink>
              ))}
            </div>
            );
          })}
        </nav>

        <div className="border-t border-[#12142b]/8 px-3 py-3">
          <div className="mb-2 px-3">
            <p className="text-sm font-semibold text-[#12142b]">{admin?.name}</p>
            <p className="text-xs text-[#565b73]">{admin?.role === "super_admin" ? "Super Admin" : admin?.role}</p>
          </div>
          <button
            data-testid="admin-logout"
            onClick={logout}
            className="flex w-full items-center gap-2 rounded-lg px-3 py-2.5 text-sm font-semibold text-[#e4002b] transition-colors hover:bg-[#e4002b]/8"
          >
            <LogOut className="h-[18px] w-[18px]" strokeWidth={1.75} /> Log out
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="ml-64 flex-1">
        <div className="sticky top-0 z-30 flex items-center justify-end border-b border-[#12142b]/6 bg-white/85 px-6 py-2 backdrop-blur">
          <NotificationBell />
        </div>
        <Outlet />
      </main>
      <Toaster position="top-right" richColors />
      <AuthorizationDialog />
    </div>
  );
}
